import { fetcher } from 'fetcher!sofe';
import { featureEnabled } from 'feature-toggles!sofe';
import { capitalize } from 'src/common/helpers/common.helpers';
import { Payment, PaymentStatusData } from 'src/@types/common';

type Recurrence =
  | {
      id: string;
      status?: string;
      amount?: string;
    }
  | {
      start_date: string | null;
      template: {
        card_id: string;
        invoice_payments: Array<{
          amount: string | number | null;
          relationships: {
            applied_to: {
              id: number;
              type: string | null;
            };
          };
        }>;
        relationships: {
          for?: {
            id: number;
            type?: string;
            name?: string;
          };
        };
        amount: string;
        credit_description: string;
        client_notes: string;
      };
    };

type PaymentSessionData = {
  payment_session_id?: string;
  client_id: string | number;
  session_details?: {
    invoice_payments?: Array<{
      invoice_id: string | number;
      amount: string | number;
    }>;
    account_balance_amount?: string | number;
    credit_description?: string;
    recurrence_id?: string;
    payment_terms?: number;
  };
  payment_data?: {
    paymentMethod?: {
      brand: string;
    };
    storePaymentMethod: boolean;
  };
  nickname?: string;
  card_id?: string;
  team_can_use?: boolean;
  is_preferred?: boolean;
  payment_preflight_id?: string;
};

type SessionDetails = {
  invoice_payments?: Array<{
    invoice_id: string | number;
    amount: string | number;
  }>;
  account_balance_amount?: string | number;
  credit_description?: string;
  recurrence_id?: string;
  payment_terms?: number;
};

type RefundData = {
  id: string;
  notes?: string;
  check_number?: string;
  date?: string;
  display_method?: string;
  payment_id: string;
  payment_method: string;
};

// Filter types
type FilterParams = {
  filter_params: string[];
  order?: string;
};

type DateFilterParams = {
  filter_params: string;
  before?: string;
  after?: string;
};

type SearchFilters = {
  client_id?: FilterParams;
  client_ids?: FilterParams;
  client_name?: FilterParams;
  payment_number?: FilterParams;
  amount?: FilterParams;
  type?: FilterParams;
  payment_type?: FilterParams;
  display_methods?: FilterParams;
  status?: FilterParams;
  start?: DateFilterParams;
  next_occurrence?: DateFilterParams;
  first_payment_occurrence?: DateFilterParams;
  next_payment_occurrence?: DateFilterParams;
  frequency?: FilterParams;
  fees?: { order: string };
  description?: { order: string };
  payment_amount?: { order: string };
  total?: { order: string };
};

export const getPayment = (params: { paymentId: string; include?: string }) => {
  return fetcher(`/api/payments/${params.paymentId}${params.include ? `?include=${params.include}` : ''}`).then(
    (response: { payments: Payment }) => response.payments
  );
};

export const getAllPayments = (filters: SearchFilters, clientId: string | null = null, page: number = 1) => {
  if (clientId && !filters.client_id) {
    filters.client_id = { filter_params: [clientId] };
  }

  return fetcher(`/api/payments:search?limit=50&page=${page}`, {
    method: 'POST',
    body: filters,
  });
};

export const getAllRefunds = (filters: SearchFilters, clientId: string | null = null, page: number = 1) => {
  if (clientId && !filters.client_id) {
    filters.client_ids = { filter_params: [clientId] };
  }
  return fetcher(`/api/refunds/search?limit=50&page=${page}`, {
    method: 'POST',
    body: filters,
  });
};

export const getPaymentCSV = (
  filters: SearchFilters,
  clientId: string | null = null,
  signal: AbortSignal | null = null
) => {
  if (clientId && !filters.client_id) {
    filters.client_id = { filter_params: [clientId] };
  }

  return fetcher('/api/payments/export', {
    method: 'POST',
    body: filters,
    signal,
  });
};

export const schedulePaymentCSV = (filters: SearchFilters, clientId: string | null = null) => {
  if (clientId && !filters.client_id) {
    filters.client_id = { filter_params: [clientId] };
  }

  return fetcher('api/payments/schedule_csv', {
    method: 'POST',
    body: filters,
  });
};

export const getRefundCSV = (filters: SearchFilters, clientId: string | null = null) => {
  if (clientId && !filters.client_ids) {
    filters.client_ids = { filter_params: [clientId] };
  }

  return fetcher(`/api/refunds/export`, {
    method: 'POST',
    body: filters,
  });
};

export function getPaymentFilter(type: string, filter: SearchFilters, sortFilterList: boolean, searchParams: string) {
  const paymentFilterBody = { search_string: searchParams, filter, sortFilterList };

  return fetcher(`/api/payments:search-filter/${type}`, {
    method: 'POST',
    body: paymentFilterBody,
  }).then((response: { [key: string]: Array<{ id: string; name: string }> }) => {
    const values = response[type];
    return values.map((value: { id: string; name: string }) => ({
      id: value.id ? value.id.toString() : value.name,
      label: type !== 'clients' ? capitalize(value.name) : value.name,
    }));
  });
}

export function getRefundFilter(type: string, filter: SearchFilters, sortFilterList: boolean, searchParams: string) {
  const refundFilterBody = { search_string: searchParams, filter, sortFilterList };

  return fetcher(`/api/refunds/search/${type}`, {
    method: 'POST',
    body: refundFilterBody,
  }).then((response: { [key: string]: Array<{ id: string; name: string }> }) => {
    const values = response[type];
    return values.map((value: { id: string; name: string }) => ({
      id: value.id ? value.id.toString() : value.name,
      label: type !== 'clients' ? capitalize(value.name) : value.name,
    }));
  });
}

export const createPayment = (payment: Payment) => {
  return fetcher('/api/payments', {
    method: 'POST',
    body: { payments: payment },
  }).then((response: { payments: Payment }) => response.payments);
};

export function savePayment(payment: Payment) {
  return fetcher(`/api/payments/${payment.id}`, {
    method: 'PUT',
    body: { payments: payment },
  }).then((response: { payments: Payment }) => response.payments);
}

export function saveScheduledPayment(params: { id: string }, scheduledPayment: { recurrences: Recurrence }) {
  return fetcher(`/api/payments/scheduled_one_off/${params.id}`, {
    method: 'PUT',
    body: scheduledPayment,
  });
}

export function refundPayment(refundData: RefundData) {
  return fetcher(`/api/refunds`, {
    method: 'POST',
    body: refundData,
  });
}

export function retryFailedRefund(refundData: RefundData) {
  return fetcher(`/api/refunds/${refundData.id}`, {
    method: 'PATCH',
    body: refundData,
  });
}

export function getRefundItems(paymentId: string) {
  return fetcher(`/api/payments/${paymentId}/refundable_items`);
}

export function updatePayment(params: { id: string }, payment: { payments: Payment }) {
  return fetcher(`/api/payments/${params.id}`, {
    method: 'PATCH',
    body: payment,
  }).then((response: { payments: Payment }) => response.payments);
}

export function deletePayment(paymentId: string) {
  return fetcher(`/api/payments/${paymentId}`, {
    method: 'DELETE',
  });
}

export function deleteScheduledPayment(paymentId: string) {
  return fetcher(`/api/payments/scheduled_one_off/${paymentId}`, {
    method: 'DELETE',
  });
}

export function printPayment(paymentId: string) {
  window.open(`/api/payments/${paymentId}?isPdf=true`, '_blank');
}

export function downloadPayment(paymentId: string) {
  location.href = `/api/payments/${paymentId}?isPdf=true&download=true`;
}

export const getPaymentPDF = (paymentId: string, asBlob: boolean = false) => {
  return fetcher(`/api/payments/${paymentId}?isPdf=true`, { responseType: asBlob ? 'blob' : 'json' });
};

export function getCanopyPaymentsStatus() {
  return fetcher('/api/canopy_payments/status').then((response: { status: PaymentStatusData }) => response.status);
}

export function getAdyenCapitalStatus() {
  return fetcher('/api/adyen_capital/status');
}

export function getPaymentReceivedData(period: string, start: string, end: string, clientId: string) {
  return fetcher(
    `/api/dashboard/payments/payments_received?period=${period}&start=${start}&end=${end}&client_id=${clientId}`
  ).then((response: { result: Array<{ amount: number; count: number }> }) => response.result);
}

export function getPaymentTypeData(start: string, end: string, clientId: string) {
  return fetcher(
    `/api/dashboard/payments/payments_received_by_type?&start=${start}&end=${end}&client_id=${clientId}`
  ).then((response: { result: Array<{ amount: number; count: number; type: string }> }) => response.result);
}

export const getAdyenPaymentDeposits = (
  searchTerm: string = '',
  page: number = 1,
  limit: string | number = 50,
  start: string = '',
  end: string = ''
) => {
  return fetcher(
    `/api/canopy_payments/payment_deposits_dashboard?page=${page}&limit=${limit}${
      searchTerm ? `&search_field=${searchTerm}` : ''
    }${start ? `&start=${start}` : ''}${end ? `&end=${end}` : ''}`
  );
};

export const getAdyenDepositCSV = (start: string, end: string, searchTerm: string = '', batchId: string = '') => {
  return fetcher(
    `/api/canopy_payments/payment_deposits_dashboard/csv?search_field=${searchTerm}&batch_id=${batchId}${
      start ? `&start=${start}` : ''
    }${end ? `&end=${end}` : ''}`
  );
};

export const getUpcomingPayments = (
  filters: SearchFilters,
  clientId: string | null = null,
  page: number = 1,
  oneTime: boolean = true
) => {
  if (clientId && !filters.client_id) {
    filters.client_name = { filter_params: [clientId] };
  }

  return fetcher(`/api/payments/recurrences:search?scheduled_only=${oneTime}&limit=50&page=${page}`, {
    method: 'POST',
    body: filters,
  });
};

export const getPaymentUpcomingCSV = (
  filters: SearchFilters,
  clientId: string | null = null,
  oneTime: boolean = true
) => {
  if (clientId && !filters.client_id) {
    filters.client_name = { filter_params: [clientId] };
  }

  return fetcher(`/api/payments/recurrences/export?scheduled_only=${oneTime}`, {
    method: 'POST',
    body: filters,
  });
};

export function getScheduledPaymentFilter(
  type: string,
  filter: SearchFilters,
  oneTime: boolean = true,
  sortFilterList: boolean,
  searchParams: string
) {
  const scheduledPaymentFilterBody = { search_string: searchParams, filter, sortFilterList };

  return fetcher(
    `/api/payments/recurrences${oneTime ? '' : '/from_invoices'}:search-filter/${type}?${
      oneTime ? 'scheduled_only' : 'with_payment_details_only'
    }=true`,
    {
      method: 'POST',
      body: scheduledPaymentFilterBody,
    }
  ).then((response: { [key: string]: Array<{ id: string; name: string }> }) => {
    const values = response[type];
    return values.map((value: { id: string; name: string }) => ({
      id: value.id ? value.id.toString() : value.name,
      label: type !== 'clients' ? capitalize(value.name) : value.name,
    }));
  });
}

export const getScheduledPayment = (params: { recurrenceId: string; include?: string }) => {
  return fetcher(
    `/api/payments/recurrences/${params.recurrenceId}${params.include ? `?include=${params.include}` : ''}`
  ).then((response: { recurrences: Recurrence }) => response.recurrences);
};

export function updatePaymentSession(payment: PaymentSessionData) {
  return fetcher('/api/canopy_payments/payment_session', {
    method: 'PUT',
    body: payment,
  });
}

export function getPayfacPaymentMethods(clientId: string) {
  return fetcher(`/api/canopy_payments/payfac_payment_methods?client_id=${clientId}`, {
    method: 'GET',
  });
}

export function processPaymentSession({
  paymentSessionId,
  paymentData,
  clientId,
  sessionDetails,
}: {
  paymentSessionId: string;
  paymentData: PaymentSessionData;
  clientId: string;
  sessionDetails?: SessionDetails;
}) {
  return fetcher(
    featureEnabled('toggle_shadow_remove_session')
      ? `/api/canopy_payments/payment/process`
      : `/api/canopy_payments/payment_session/${paymentSessionId}/process`,
    {
      method: 'POST',
      body: {
        ...paymentData,
        ...(featureEnabled('toggle_shadow_remove_session') && {
          client_id: clientId,
          session_details: sessionDetails || {},
        }),
      },
    }
  );
}

export const updateCanopyPayment = ({ cardId, paymentData }: { cardId: string; paymentData: PaymentSessionData }) => {
  return fetcher(`/api/card_management/${cardId}`, {
    method: 'PATCH',
    body: paymentData,
  });
};

export const deleteAdyenPaymentMethod = (paymentMethodId: string) => {
  return fetcher(`/api/card_management/${paymentMethodId}`, {
    method: 'DELETE',
  });
};

export function getSurchargeFee(paymentData: { amount: string; payment_type: string }) {
  return fetcher('/api/canopy_payments/calculate_fees', {
    method: 'POST',
    body: paymentData,
  });
}

export function updateSecureLinkPaymentSession(objectType: string, objectId: string, payment: PaymentSessionData) {
  return fetcher(`/api/canopy_payments/payment_session/object_type/${objectType}/object_id/${objectId}`, {
    method: 'PUT',
    body: payment,
  });
}

export function processSecureLinkPaymentSession(paymentData: PaymentSessionData, version: number) {
  return fetcher(
    featureEnabled('toggle_shadow_remove_session') && version === 2
      ? '/api/canopy_payments/payment/secure_process'
      : '/api/canopy_payments/payment_session/secure_process',
    {
      method: 'POST',
      body: paymentData,
    }
  );
}

export function getPaymentPreflightId(clientId: string) {
  return fetcher(`/api/canopy_payments/preflight/client/${clientId}`, {
    method: 'POST',
  });
}
